import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE'

interface ApiOptions {
  method?: HttpMethod
  body?: unknown
  headers?: Record<string, string>
}

export function useApi() {
  const { token } = useAuth()

  const request = useCallback(
    async <T>(endpoint: string, options: ApiOptions = {}): Promise<T> => {
      const { method = 'GET', body, headers = {} } = options

      const config: RequestInit = {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...headers,
        },
      }

      if (body && method !== 'GET') {
        config.body = JSON.stringify(body)
      }

      const response = await fetch(endpoint, config)

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }))
        throw new Error(error.error || `HTTP ${response.status}`)
      }

      return response.json()
    },
    [token]
  )

  const get = useCallback(
    <T>(endpoint: string) => request<T>(endpoint, { method: 'GET' }),
    [request]
  )

  const post = useCallback(
    <T>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: 'POST', body }),
    [request]
  )

  return { request, get, post }
}
